import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  NavLink,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';

import { useSite } from '../hooks/useSites';
import { useBreadcrumbLabel } from '../contexts/BreadcrumbContext';
import { useAuth } from '../auth/auth0-provider';
import { SITE_STATUS } from '../constants/siteStatus';
import { useSiteActions } from '../hooks/useSiteActions';
import { useStuckPublishing } from '../hooks/useStuckPublishing';
import { useDeleteSite } from '../hooks/useDeleteSite';
import { useCancelSubscription } from '../hooks/useCancelSubscription';
import { syncSiteSubscription } from '@/api/billingApi';
import { useToast } from '@/contexts/ToastContext';
import { formatPriceCta } from '../utils/formatPrice';
import { Time } from '@/components/ui/time';

import { APP_CONFIG } from '../config/app.config';
import { SiteActionsDropdown } from '../components/sites/SiteActionsDropdown';
import { SiteStatusBadge } from '../components/sites/SiteStatusBadge';
import { getConfig } from '../components/sites/siteDisplay';
import { DeleteSiteDialog } from '../components/sites/DeleteSiteDialog';

import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { Skeleton } from '@/components/ui/skeleton';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

import type { SiteWithTemplate } from '../types/template';

/**
 * Parent route for `/sites/:siteId` and its tab children. Fetches the site
 * once via TanStack, renders the shared header + tab nav, owns the
 * Stripe-return effect and the delete dialog, and exposes the narrowed
 * `SiteDetailContext` to the tab children via Outlet context.
 *
 * See: docs/superpowers/specs/2026-04-30-site-detail-tabs-design.md
 */

// `templateTier` may be unknown (no joined template row); default to standard.
const monthlyPriceFor = (tier: 'standard' | 'premium' | undefined): number =>
  tier === 'premium' ? 7 : 5;

// `site` is guaranteed non-null at this point — the layout renders the
// `<Outlet>` only after the loading/error/null guards above. Narrowing the
// type here saves every tab from re-checking it.
export type SiteDetailContext = {
  site: NonNullable<SiteWithTemplate>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  subdomain: string;
  domain: string;
  hasActiveSub: boolean;
  monthlyPrice: number;
  paid: boolean;
};

export const SiteRoute: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { isAuthenticated, isSdkReady } = useAuth();

  const {
    data: siteData,
    isLoading,
    error: queryError,
    refetch,
  } = useSite(siteId || '');

  const site: SiteWithTemplate | null = siteData ?? null;
  // Treat the auth-init window as loading. Otherwise TanStack returns
  // `isLoading: false, data: undefined` while the query is disabled, which
  // would flash the not-found error screen below.
  const loading = isLoading || !isAuthenticated || !isSdkReady;
  const error = queryError instanceof Error
    ? queryError.message
    : queryError
      ? String(queryError)
      : null;
  const refetchVoid = () => {
    void refetch();
  };

  // Site has been in `publishing` >5min — drop the badge pulse + show retry.
  const stuckPublishing = useStuckPublishing(site);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const deleteSite = useDeleteSite();
  const cancelSubscription = useCancelSubscription();

  const {
    handlePublish,
    handleRepublish,
    isPublishing,
    isRepublishing,
  } = useSiteActions(siteId || '', {
    onSubscriptionChange: () => refetchVoid(),
    onDeleted: () => navigate('/sites'),
    onPublished: () => refetchVoid(),
  });

  // Handle return-from-Stripe redirect query params.
  //
  // Stripe redirects the browser before the `checkout.session.completed`
  // webhook is reliably delivered (and webhooks can fail entirely in dev /
  // outage scenarios). Instead of trusting the webhook, we POST to the
  // backend's sync-subscription endpoint, which queries Stripe directly,
  // flips the row to `live` if there's an active sub for a `site-checkout`
  // flow, AND dispatches the deploy workflow. Retried with backoff because
  // Stripe's subscription list can lag the redirect by a beat. The webhook
  // and the sync race — both are idempotent and the conditional UPDATE
  // ensures only the first writer dispatches the deploy.
  const stripeReturnHandled = useRef<string | null>(null);
  // Cancellation for the sync loop below. The loop's lifetime is decoupled
  // from the effect that starts it: the effect re-runs when it strips the
  // success flag from the URL (searchParams is in its dep array), and an
  // effect-cleanup `cancelled` flag would kill the loop mid-retry on that
  // re-run. Instead the loop checks a state object held in this ref, and
  // ONLY the unmount/siteId-change effect further below flips it.
  const stripeSyncState = useRef<{ cancelled: boolean } | null>(null);
  useEffect(() => {
    const successFlag = ['published', 'upgraded', 'restored'].find(
      (k) => searchParams.get(k) === 'true',
    );
    if (!successFlag || !siteId) return;
    // Guard against double-runs from React Strict Mode / re-renders.
    const handleKey = `${siteId}:${successFlag}`;
    if (stripeReturnHandled.current === handleKey) return;
    stripeReturnHandled.current = handleKey;

    const messages: Record<string, { title: string; message: string }> = {
      published: { title: 'Payment confirmed', message: 'Publishing your site now.' },
      upgraded: { title: 'Upgrade complete', message: 'Custom domain support enabled.' },
      restored: { title: 'Site restored', message: 'Coming back online shortly.' },
    };
    const m = messages[successFlag];
    if (m) toast.success(m.message, m.title);

    // Drop the flag from the URL immediately so a refresh doesn't re-pop
    // the toast. The ref guard above already prevents this effect from
    // re-running for the same flag, so the searchParams change is harmless.
    const next = new URLSearchParams(searchParams);
    next.delete(successFlag);
    setSearchParams(next, { replace: true });

    // Sync with backoff: 0s, 1.5s, 4s. Stop as soon as Stripe surfaces the
    // sub. `synced: true` (whether or not WE flipped the row) means the
    // backend has reconciled — refetch the row and we're done.
    const RETRY_DELAYS_MS = [0, 1500, 4000];
    const loop = { cancelled: false };
    stripeSyncState.current = loop;
    (async () => {
      for (const delay of RETRY_DELAYS_MS) {
        if (loop.cancelled) return;
        if (delay > 0) await new Promise((r) => setTimeout(r, delay));
        if (loop.cancelled) return;
        const result = await syncSiteSubscription(siteId);
        if (!result.success) continue;
        if (result.data?.synced) {
          refetchVoid();
          return;
        }
      }
      // All retries exhausted without finding the subscription. Refetch
      // anyway so any partial state the webhook DID write is visible.
      if (!loop.cancelled) refetchVoid();
    })();

    // No cleanup here on purpose — see stripeSyncState above. The
    // stripeReturnHandled guard ensures re-runs of this effect don't start
    // a second loop for the same flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, siteId, toast]);

  // Cancel the in-flight sync loop only when it can no longer act on this
  // site: unmount or siteId change. Deliberately NOT tied to searchParams.
  useEffect(() => {
    return () => {
      if (stripeSyncState.current) stripeSyncState.current.cancelled = true;
    };
  }, [siteId]);

  const config = useMemo(
    () => (site ? (getConfig(site) as Record<string, unknown>) : {}),
    [site],
  );

  // Resolve a human-readable label for the parent breadcrumb crumb. Falls
  // back to the site's published hostname; if neither is available the
  // breadcrumb config's default ("Site details") is used.
  const breadcrumbLabel = ((): string | undefined => {
    if (!site) return undefined;
    const settings = config;
    if (settings.domain) return settings.domain as string;
    if (settings.subdomain) return `${settings.subdomain}.${APP_CONFIG.SITES_DOMAIN}`;
    if (site.subdomain) return `${site.subdomain}.${APP_CONFIG.SITES_DOMAIN}`;
    return undefined;
  })();

  useBreadcrumbLabel(breadcrumbLabel, siteId ? `/sites/${siteId}` : undefined);

  // ---- Early returns ------------------------------------------------------

  if (!siteId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-medium text-foreground mb-2">Invalid site</h2>
          <p className="text-sm text-muted-foreground mb-4">No site ID provided.</p>
          <Button variant="primary" size="dense" onClick={() => navigate('/sites')}>
            Go to sites
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="site-detail min-h-screen">
        <div className="max-w-7xl mx-auto pt-5 pb-8">
          <Breadcrumbs />
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-10 w-64 bg-foreground/5" />
                  <Skeleton className="h-9 w-24 bg-foreground/5" />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Skeleton className="h-6 w-20 rounded bg-foreground/5 mr-2" />
                  <Skeleton className="h-4 w-48 bg-foreground/5" />
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-12">
            <section className="flex gap-8">
              <div className="w-48 flex-shrink-0">
                <Skeleton className="h-7 w-24 bg-foreground/5" />
              </div>
              <div className="flex-1 min-w-0 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center">
                    <Skeleton className="h-4 w-28 mr-4 bg-foreground/5" />
                    <Skeleton className="h-4 w-48 bg-foreground/5" />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-destructive text-3xl mb-4">!</div>
          <h2 className="text-2xl font-medium text-foreground mb-2">Site not found</h2>
          <p className="text-sm text-muted-foreground mb-4">{error || 'Could not load site details.'}</p>
          <div className="flex items-center justify-center space-x-3">
            <Button size="dense" variant="tertiary" onClick={() => navigate('/sites')}>
              Go to sites
            </Button>
            <Button size="dense" variant="primary" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Derived state -----------------------------------------------------

  const subdomain = (config.subdomain as string) || site.subdomain || '';
  const domain = (config.domain as string) || '';
  const description = (config.description as string) || '';

  const tier = (site.template?.tier as 'standard' | 'premium' | undefined) ?? 'standard';
  const monthlyPrice = monthlyPriceFor(tier);
  const hasActiveSub =
    !!site.stripeSubscriptionId &&
    (site.subscriptionStatus === 'active' || site.subscriptionStatus === 'trialing');
  const paid = hasActiveSub || !!site.paymentRequired;

  const getDisplayName = (): string => {
    if (domain) return domain;
    if (subdomain) return `${subdomain}.${APP_CONFIG.SITES_DOMAIN}`;
    return 'Unnamed site';
  };

  /**
   * Primary CTA rendered next to the Actions dropdown in the header.
   * DRAFT → Publish, SUSPENDED → Restore, OFFLINE/FAILED/stalled-publishing → Re-publish.
   */
  const renderHeaderCta = (): React.ReactNode => {
    if (site.status === SITE_STATUS.FAILED || stuckPublishing) {
      return (
        <LoadingButton
          variant="primary"
          size="default"
          onClick={handleRepublish}
          loading={isRepublishing}
          loadingText={paid ? 'Opening checkout…' : 'Re-publishing…'}
        >
          {paid ? `Re-publish${formatPriceCta(monthlyPrice)}` : 'Re-publish'}
          <ArrowRight className="h-4 w-4 stroke-[1.5]" />
        </LoadingButton>
      );
    }
    if (site.status === SITE_STATUS.DRAFT) {
      return (
        <LoadingButton
          variant="primary"
          size="default"
          onClick={handlePublish}
          loading={isPublishing}
          loadingText={paid ? 'Opening checkout…' : 'Publishing…'}
        >
          {paid ? `Publish${formatPriceCta(monthlyPrice)}` : 'Publish (free)'}
          <ArrowRight className="h-4 w-4 stroke-[1.5]" />
        </LoadingButton>
      );
    }
    if (site.status === SITE_STATUS.SUSPENDED) {
      return (
        <LoadingButton
          variant="primary"
          size="default"
          onClick={handleRepublish}
          loading={isRepublishing}
          loadingText="Opening checkout…"
        >
          Restore{formatPriceCta(monthlyPrice)}
          <ArrowRight className="h-4 w-4 stroke-[1.5]" />
        </LoadingButton>
      );
    }
    if (site.status === SITE_STATUS.OFFLINE) {
      return (
        <LoadingButton
          variant="primary"
          size="default"
          onClick={handleRepublish}
          loading={isRepublishing}
          loadingText={paid ? 'Opening checkout…' : 'Re-publishing…'}
        >
          {paid ? `Re-publish${formatPriceCta(monthlyPrice)}` : 'Re-publish (free)'}
          <ArrowRight className="h-4 w-4 stroke-[1.5]" />
        </LoadingButton>
      );
    }
    return null;
  };

  /**
   * Confirmed delete from the modal — bypasses the inline confirm dialogs in
   * useSiteActions because the modal IS the confirmation. Cancels an active
   * sub first (backend gate permits DELETE once `cancel_at_period_end=true`)
   * then deletes the site. `useDeleteSite.onSuccess` already invalidates
   * `['sites']` and removes `['sites', siteId]` — no manual invalidation here.
   */
  const handleConfirmedDelete = async () => {
    try {
      if (hasActiveSub && !site.subscriptionCancelAtPeriodEnd) {
        await cancelSubscription.mutateAsync(site.id);
      }
      await deleteSite.mutateAsync(site.id);
      navigate('/sites');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete site';
      toast.error(message, 'Delete failed');
    }
  };

  const childCtx: SiteDetailContext = {
    site,
    loading,
    error,
    refetch: refetchVoid,
    subdomain,
    domain,
    hasActiveSub,
    monthlyPrice,
    paid,
  };

  return (
    <div className="site-detail min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-8">
        <Breadcrumbs />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
                  {getDisplayName()}
                </h1>
                {(() => {
                  // Resolve the header CTA once so we can both reorder the
                  // row (CTA last) and demote Manage site to tertiary when a
                  // primary CTA is present — keeping exactly one primary
                  // affordance per row.
                  const headerCta = renderHeaderCta();
                  return (
                    <div className="flex items-center gap-2">
                      {site.status === SITE_STATUS.LIVE && (site.url || subdomain) && (
                        <Button variant="tertiary" size="default" asChild>
                          <a
                            href={site.url || `https://${subdomain}.${APP_CONFIG.SITES_DOMAIN}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View site
                            <ExternalLink className="h-4 w-4 stroke-[1.5]" />
                          </a>
                        </Button>
                      )}
                      <SiteActionsDropdown
                        site={{
                          id: site.id,
                          status: site.status,
                          url: site.url,
                          subdomain,
                        }}
                        onDelete={() => setDeleteDialogOpen(true)}
                        onEdit={() => navigate(`/sites/${site.id}/edit`)}
                        triggerVariant="button"
                        buttonTone={headerCta ? 'tertiary' : 'primary'}
                      />
                      {headerCta}
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                <SiteStatusBadge
                  status={site.status}
                  className="mr-2"
                  lastError={site.lastError}
                  stalled={stuckPublishing}
                />
                {description && (
                  <>
                    <span>{description}</span>
                    <span>-</span>
                  </>
                )}
                <span>Created <Time value={site.createdAt} format="medium" /></span>
              </div>
            </div>
          </div>

          {/* Tab nav — temporarily hidden while we evaluate a single-page
              overview that stacks analytics + overview + activity log.
              Routes (/overview, /activity, /analytics) remain wired in
              App.tsx so deep links still work; the nav itself just isn't
              rendered. Restore by removing the `hidden` class.
              Pill-bar styling: leftmost tab gets a fully-rounded left
              edge, rightmost the right, middles square so the strip reads
              as one continuous pill. */}
          <nav className="hidden" role="tablist">
            {(() => {
              const tabs = [
                { to: 'overview',  label: 'Overview' },
                { to: 'activity',  label: 'Activity log' },
                { to: 'analytics', label: 'Analytics' },
              ];
              return tabs.map(({ to, label }, i) => {
                const cornerClass =
                  i === 0
                    ? 'rounded-l-full'
                    : i === tabs.length - 1
                      ? 'rounded-r-full'
                      : '';
                return (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      [
                        'px-4 py-2 text-sm font-medium transition-colors',
                        cornerClass,
                        isActive
                          ? 'bg-foreground text-background'
                          : 'bg-foreground/5 text-foreground hover:bg-foreground/10',
                      ].join(' ')
                    }
                  >
                    {label}
                  </NavLink>
                );
              });
            })()}
          </nav>
        </div>

        {/* Tab content */}
        <Outlet context={childCtx} />
      </div>

      <DeleteSiteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        hasActiveSub={hasActiveSub}
        cancelAtPeriodEnd={!!site.subscriptionCancelAtPeriodEnd}
        currentPeriodEnd={site.subscriptionCurrentPeriodEnd ?? null}
        onConfirm={handleConfirmedDelete}
      />
    </div>
  );
};

export default SiteRoute;
